/**
 * Convert an HTML fragment into plain text that reads well inside a single spreadsheet cell.
 *
 * CSV has no styling primitives — bold, colour, and font are lost by definition. This converter
 * preserves what text-only structure *can* survive:
 *
 *   - Block boundaries (`<p>`, `<div>`, `<h1..6>`, `<blockquote>`, table cells) become newlines.
 *   - `<br>` becomes a single newline.
 *   - `<li>` inside `<ul>` becomes `• <text>` on its own line; inside `<ol>` it becomes `1. <text>`.
 *   - `<a href="https://...">link</a>` becomes `link (https://...)` unless the anchor text already
 *     equals the href (avoids `https://x (https://x)` noise).
 *   - HTML entities (`&amp;`, `&nbsp;`, `&#39;` etc.) are decoded automatically by the DOM parser.
 *
 * The implementation prefers `DOMParser` (available in browsers and in jsdom, which CRA tests use)
 * and falls back to a regex strip for exotic runtime environments (SSR, workers without DOM).
 */

const BLOCK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'div',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'main',
  'nav',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr'
]);

const SKIP_TAGS = new Set(['script', 'style', 'noscript', 'template']);

export function htmlToPlainText(input: string | null | undefined): string {
  if (input === null || input === undefined) return '';
  const str = String(input);
  if (str === '') return '';

  // Fast path — no markup, nothing to strip. Still decode stray entities and run the same
  // whitespace normalisation so the caller gets consistent output regardless of input shape.
  if (!/<[a-zA-Z!/]/.test(str)) {
    return normalise(decodeEntitiesFast(str));
  }

  // DOM path
  if (typeof DOMParser !== 'undefined' && typeof document !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(
        `<div id="__csv_root__">${str}</div>`,
        'text/html'
      );
      const root = doc.getElementById('__csv_root__');
      if (root) {
        const out: string[] = [];
        walk(root, out);
        return normalise(out.join(''));
      }
    } catch {
      /* fall through */
    }
  }

  // Regex fallback: strip tags, decode basic entities, collapse whitespace.
  return normalise(
    decodeEntitiesFast(
      str
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, '\n')
        .replace(/<li[^>]*>/gi, '\n• ')
        .replace(/<[^>]+>/g, '')
    )
  );
}

interface WalkState {
  // list stack: 'ul' uses bullets, 'ol' uses numbers. Each frame tracks the running counter.
  listStack: Array<{ kind: 'ul' | 'ol'; counter: number }>;
}

function walk(
  node: Node,
  out: string[],
  state: WalkState = { listStack: [] }
): void {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push(node.textContent || '');
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if (SKIP_TAGS.has(tag)) return;

  if (tag === 'br') {
    out.push('\n');
    return;
  }

  if (tag === 'a') {
    const href = (el.getAttribute('href') || '').trim();
    const text = (el.textContent || '').trim();
    if (!text && !href) return;
    if (!text) {
      out.push(href);
    } else if (!href || stripUrl(href) === stripUrl(text)) {
      out.push(text);
    } else {
      out.push(`${text} (${href})`);
    }
    return;
  }

  if (tag === 'ul' || tag === 'ol') {
    out.push('\n');
    state.listStack.push({ kind: tag, counter: 0 });
    for (const child of Array.from(el.childNodes)) walk(child, out, state);
    state.listStack.pop();
    out.push('\n');
    return;
  }

  if (tag === 'li') {
    const frame = state.listStack[state.listStack.length - 1];
    const marker = frame
      ? frame.kind === 'ol'
        ? `${++frame.counter}. `
        : '• '
      : '• ';
    out.push('\n' + marker);
    for (const child of Array.from(el.childNodes)) walk(child, out, state);
    return;
  }

  const isBlock = BLOCK_TAGS.has(tag);
  if (isBlock) out.push('\n');
  for (const child of Array.from(el.childNodes)) walk(child, out, state);
  if (isBlock) out.push('\n');
}

/**
 * Collapse repeated whitespace while keeping line breaks between blocks. The DOM walker emits a lot
 * of leading/trailing newlines around nested blocks; we flatten those to at most one blank line.
 */
function normalise(text: string): string {
  return text
    .replace(/ /g, ' ') // non-breaking space → regular space (filterable in Excel)
    .replace(/[ \t\f\v]+\n/g, '\n') // strip trailing horizontal whitespace on each line
    .replace(/\n[ \t\f\v]+/g, '\n') // strip leading horizontal whitespace on each line
    .replace(/\n{3,}/g, '\n\n') // at most one blank line
    .replace(/[ \t]{2,}/g, ' ') // collapse runs of spaces/tabs within a line
    .trim();
}

/**
 * Decode entities without constructing a full DOM tree. Browsers decode everything a `textarea`
 * can hold — the safest general-purpose decoder that has no XSS surface (no script execution).
 */
function decodeEntitiesFast(s: string): string {
  if (!/&[a-z#0-9]+;/i.test(s)) return s;
  if (typeof document === 'undefined') {
    // Minimal fallback for pure Node envs (rare; jsdom covers test runs).
    return s
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }
  const ta = document.createElement('textarea');
  ta.innerHTML = s;
  return ta.value;
}

/** Compare URLs ignoring trailing slashes and protocol-only differences. */
function stripUrl(u: string): string {
  return u.replace(/\/+$/, '').toLowerCase();
}
